import type { StoreHit } from '@/lib/algolia/stores';

/**
 * Map locale slug to Algolia localized field suffix.
 *
 * Example Input:
 * langSlug="zh"
 *
 * Example Output:
 * "zh_Hans"
 */
export function getLangSlugForAlgolia(langSlug: string): string {
  if (langSlug === 'zh') return 'zh_Hans';
  if (langSlug === 'zht') return 'zh_Hant';
  return langSlug;
}

/**
 * Resolve a localized store field from an Algolia hit.
 *
 * Example Input:
 * hit={ name: "Store", name_en: "Store EN" }, key="name", langSlug="en"
 *
 * Example Output:
 * "Store EN"
 */
export function getTranslatedStoreValue<K extends keyof StoreHit>(
  hit: StoreHit | undefined | null,
  key: K,
  langSlug: string
): string {
  if (!hit) return '';

  const localizedKey = `${String(key)}_${getLangSlugForAlgolia(langSlug)}`;
  const localizedValue = hit[localizedKey];
  if (typeof localizedValue === 'string' && localizedValue) {
    return localizedValue;
  }

  const fallback = hit[key];
  return typeof fallback === 'string' ? fallback : '';
}

const STORE_TYPE_ORDER: Record<string, number> = {
  FLAG_SHIP_BOUTIQUE: 1,
  BOUTIQUE: 2,
  OFFICIAL_RETAILER: 3,
};

const OPERATING_TYPE_ORDER: Record<string, number> = {
  Internal: 0,
  External: 1,
};

/**
 * Sort stores by boutique type and operating type.
 *
 * Example Input:
 * stores=[{ type: "BOUTIQUE" }, { type: "FLAG_SHIP_BOUTIQUE" }]
 *
 * Example Output:
 * [{ type: "FLAG_SHIP_BOUTIQUE" }, { type: "BOUTIQUE" }]
 */
export function reorderStores(stores: StoreHit[]): StoreHit[] {
  return [...stores].sort((a, b) => {
    const aType = a.type ?? '';
    const bType = b.type ?? '';

    if (aType && bType && STORE_TYPE_ORDER[aType] !== STORE_TYPE_ORDER[bType]) {
      return STORE_TYPE_ORDER[aType] - STORE_TYPE_ORDER[bType];
    }

    if (aType === 'FLAG_SHIP_BOUTIQUE' || aType === 'BOUTIQUE') {
      const aOp = a.operating_type ?? '';
      const bOp = b.operating_type ?? '';
      if (aOp && bOp && OPERATING_TYPE_ORDER[aOp] !== OPERATING_TYPE_ORDER[bOp]) {
        return OPERATING_TYPE_ORDER[aOp] - OPERATING_TYPE_ORDER[bOp];
      }
    }

    return 0;
  });
}
